import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useImportApi } from "./importApi";
import ConfirmModal from "./ConfirmModal";
import { ContactList, ContactsCell, contactCounts } from "./LeadContacts";
import {
	batchLeadRows,
	batchOutcome,
	leadRowChip,
	fmtDateTime,
	IrisLeadLink,
	StatusChip,
	TERMINAL_BATCH_STATUSES,
	toastApiError,
	useBatchPolling,
	useMountedRef,
} from "./common";
import "./Import.css";

// Normalize POST /import/batches/{id}/revert-scan output
// ({auto_revertible[], conflicts[], deleted[]} — item shape not pinned by §5).
function normalizeScan(raw) {
	const arr = (v) => (Array.isArray(v) ? v : []);
	const leadOf = (item) => ({
		importLeadId: item?.import_lead_id ?? item?.id,
		irisLeadId: item?.iris_lead_id ?? item?.lead_id ?? null,
		groupKey: String(item?.group_key ?? item?.company ?? ""),
		action: item?.action ?? "created",
	});

	const auto = arr(raw?.auto_revertible).map(leadOf);
	const deleted = arr(raw?.deleted ?? raw?.deleted_externally).map(leadOf);
	const conflicts = arr(raw?.conflicts).map((item) => {
		const base = leadOf(item);
		const rawFields =
			item?.fields ?? item?.diffs ?? item?.changed_fields ?? item?.conflicts;
		const fields = arr(rawFields).map((f, i) => ({
			label: String(
				f?.label ??
					f?.field_label ??
					f?.name ??
					(f?.field && typeof f.field === "object"
						? f.field.label ?? f.field.id
						: f?.field) ??
					f?.field_id ??
					`field ${i + 1}`
			),
			importedValue: String(
				f?.value_at_import ?? f?.imported ?? f?.post_value ?? f?.was ?? ""
			),
			currentValue: String(
				f?.current_value ?? f?.current ?? f?.now ?? f?.changed_to ?? ""
			),
			changedBy:
				f?.changed_by && typeof f.changed_by === "object"
					? String(f.changed_by.name ?? f.changed_by.id ?? "")
					: String(f?.changed_by ?? ""),
			source: String(f?.source ?? ""),
		}));
		return { ...base, fields };
	});

	return { auto, conflicts, deleted };
}

function leadDisplayName(item) {
	if (item.groupKey) return item.groupKey;
	if (item.irisLeadId !== null && item.irisLeadId !== undefined) {
		return `Iris lead ${item.irisLeadId}`;
	}
	return `Import row ${item.importLeadId}`;
}

export default function ImportHistory({ active, focusBatchId }) {
	const api = useImportApi();
	const mountedRef = useMountedRef();

	const [batches, setBatches] = useState(null); // null = never loaded
	const [listLoading, setListLoading] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [pollKey, setPollKey] = useState(0);

	// Revert state
	const [scan, setScan] = useState(null);
	const [scanLoading, setScanLoading] = useState(false);
	const [decisions, setDecisions] = useState({});
	const [checked, setChecked] = useState(() => new Set());
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [revertBusy, setRevertBusy] = useState(false);
	const [downloadBusy, setDownloadBusy] = useState(false);
	// Lead rows whose contacts are expanded (row id); reset per batch.
	const [openContacts, setOpenContacts] = useState(() => new Set());

	const resetRevertState = useCallback(() => {
		setScan(null);
		setDecisions({});
		setChecked(new Set());
		setConfirmOpen(false);
	}, []);

	const loadBatches = useCallback(async () => {
		setListLoading(true);
		try {
			const data = await api.listBatches();
			if (!mountedRef.current) return;
			setBatches(Array.isArray(data) ? data : data?.batches || []);
		} catch (err) {
			toastApiError(err, "Failed to load import batches");
		} finally {
			if (mountedRef.current) setListLoading(false);
		}
	}, [api, mountedRef]);

	// Refresh the list every time the History view becomes visible.
	useEffect(() => {
		if (active) loadBatches();
	}, [active, loadBatches]);

	// Jump straight to a batch when the wizard hands one over.
	useEffect(() => {
		if (focusBatchId !== null && focusBatchId !== undefined) {
			setSelectedId(focusBatchId);
			resetRevertState();
		}
	}, [focusBatchId, resetRevertState]);

	// Poll the selected batch (keeps running during execute/revert jobs).
	const { batch } = useBatchPolling(api, selectedId, {
		enabled: active && selectedId !== null,
		refreshKey: pollKey,
	});

	// Keep the list row in sync with the polled detail.
	useEffect(() => {
		if (!batch || batch.id === null || batch.id === undefined) return;
		setBatches((prev) =>
			prev
				? prev.map((b) =>
						String(b.id) === String(batch.id)
							? {
									...b,
									status: batch.status,
									imported: batch.imported,
									failed: batch.failed,
									total_leads: batch.total_leads,
									params: batch.params ?? b.params,
									finished_at: batch.finished_at,
							  }
							: b
				  )
				: prev
		);
	}, [batch]);

	// Toast once when a revert we started reaches a terminal state.
	// Track {id, status} so switching between batches can't fake a transition.
	const prevStatusRef = useRef({ id: null, status: null });
	useEffect(() => {
		if (!batch || batch.id === null || batch.id === undefined) return;
		const prev = prevStatusRef.current;
		const now = batch.status;
		if (
			String(prev.id) === String(batch.id) &&
			prev.status === "reverting" &&
			(now === "reverted" || now === "partially_reverted")
		) {
			toast.success(`Revert finished: ${now.replace(/_/g, " ")}.`);
			loadBatches();
		}
		prevStatusRef.current = { id: batch.id, status: now };
	}, [batch, loadBatches]);

	const selectBatch = (id) => {
		setSelectedId(id);
		resetRevertState();
		setOpenContacts(new Set());
	};

	const handleDownloadFile = async () => {
		setDownloadBusy(true);
		try {
			await api.downloadBatchFile(selectedId);
		} catch (err) {
			if (err?.status === 404) {
				toast.error(err.message || "Original file no longer available for this batch.");
			} else {
				toastApiError(err, "Failed to download original CSV");
			}
		} finally {
			if (mountedRef.current) setDownloadBusy(false);
		}
	};

	const startScan = async () => {
		setScanLoading(true);
		try {
			const raw = await api.revertScan(selectedId);
			if (!mountedRef.current) return;
			const norm = normalizeScan(raw);
			setScan(norm);
			// Default every conflict to the safe choice: Skip.
			const initial = {};
			norm.conflicts.forEach((c) => {
				initial[c.importLeadId] = "skip";
			});
			setDecisions(initial);
			setChecked(new Set());
			if (
				norm.auto.length === 0 &&
				norm.conflicts.length === 0 &&
				norm.deleted.length === 0
			) {
				toast.info("Revert scan found nothing to revert in this batch.");
			}
		} catch (err) {
			toastApiError(err, "Revert scan failed");
		} finally {
			if (mountedRef.current) setScanLoading(false);
		}
	};

	const setDecision = (importLeadId, decision) => {
		setDecisions((prev) => ({ ...prev, [importLeadId]: decision }));
	};

	const toggleChecked = (importLeadId) => {
		setChecked((prev) => {
			const next = new Set(prev);
			if (next.has(importLeadId)) next.delete(importLeadId);
			else next.add(importLeadId);
			return next;
		});
	};

	const allConflictIds = scan ? scan.conflicts.map((c) => c.importLeadId) : [];
	const allChecked =
		allConflictIds.length > 0 &&
		allConflictIds.every((id) => checked.has(id));

	const toggleCheckAll = () => {
		setChecked(allChecked ? new Set() : new Set(allConflictIds));
	};

	const bulkSetDecision = (decision) => {
		if (checked.size === 0) {
			toast.warn("Select at least one conflict first.");
			return;
		}
		setDecisions((prev) => {
			const next = { ...prev };
			checked.forEach((id) => {
				next[id] = decision;
			});
			return next;
		});
	};

	const overwriteCount = Object.values(decisions).filter(
		(d) => d === "overwrite"
	).length;
	const skipCount = Object.values(decisions).filter((d) => d === "skip").length;

	const submitRevert = async () => {
		setRevertBusy(true);
		try {
			await api.revert(selectedId, decisions);
			toast.success("🔄 Revert started.");
			if (!mountedRef.current) return;
			resetRevertState();
			setPollKey((k) => k + 1); // re-arm polling: status is now "reverting"
		} catch (err) {
			toastApiError(err, "Failed to start revert");
		} finally {
			if (mountedRef.current) setRevertBusy(false);
		}
	};

	// ---- Derived detail bits ----
	const rows = batchLeadRows(batch);
	const contactTotals = contactCounts(rows);
	const rowsWithContacts = rows.filter((r) => (r.contacts || []).length > 0);
	const allContactsOpen =
		rowsWithContacts.length > 0 && rowsWithContacts.every((r) => openContacts.has(r.id));
	const toggleContacts = (key) =>
		setOpenContacts((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	const toggleAllContacts = () =>
		setOpenContacts(allContactsOpen ? new Set() : new Set(rowsWithContacts.map((r) => r.id)));
	const status = batch?.status;
	const outcome = batchOutcome(batch);
	const canScan =
		!!batch &&
		TERMINAL_BATCH_STATUSES.has(status) &&
		status !== "reverted" &&
		(batch.imported ?? 0) > 0;

	return (
		<div className="historyLayout">
			{/* -------- Batch list -------- */}
			<div className="importCard">
				<div className="batchDetailHeader">
					<h2 style={{ margin: 0 }}>📜 Import History</h2>
					<button
						type="button"
						className="importBtn secondary small"
						onClick={loadBatches}
						disabled={listLoading}>
						{listLoading ? "Refreshing…" : "Refresh"}
					</button>
				</div>

				{batches === null && (
					<p className="importMuted">🔄 Loading batches…</p>
				)}
				{batches !== null && batches.length === 0 && (
					<p className="importMuted" style={{ fontStyle: "italic" }}>
						No import batches yet — run your first import from the New Import
						tab.
					</p>
				)}
				{batches !== null && batches.length > 0 && (
					<div className="importTableWrap">
						<table className="importTable">
							<thead>
								<tr>
									<th>#</th>
									<th>File</th>
									<th>Uploaded by</th>
									<th>Created</th>
									<th>Status</th>
									<th>Result</th>
								</tr>
							</thead>
							<tbody>
								{batches.map((b) => {
									const o = batchOutcome(b);
									return (
									<tr
										key={b.id}
										className={`clickableRow ${
											String(b.id) === String(selectedId) ? "selectedRow" : ""
										}`}
										onClick={() => selectBatch(b.id)}>
										<td>{b.id}</td>
										<td>
											{b.params?.mode === "append_contacts" && (
												<span
													className="statusChip"
													title="Added contacts to existing leads"
													style={{ marginRight: 6 }}>
													add contacts
												</span>
											)}
											{b.filename ?? "—"}
										</td>
										<td>{b.uploaded_by ?? "—"}</td>
										<td>{fmtDateTime(b.created_at)}</td>
										<td>
											<StatusChip status={b.status} {...o.chip} />
										</td>
										<td>{o.short}</td>
									</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* -------- Batch detail -------- */}
			{selectedId !== null && (
				<div className="importCard">
					<div className="batchDetailHeader">
						<h2 style={{ margin: 0 }}>
							Batch #{selectedId}
							{batch?.filename ? ` — ${batch.filename}` : ""}
						</h2>
						{batch && (
							<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
								<StatusChip status={status} {...outcome.chip} />
								<button
									type="button"
									className="importBtn secondary small"
									onClick={handleDownloadFile}
									disabled={downloadBusy}>
									{downloadBusy ? "Downloading…" : "⬇️ Download original CSV"}
								</button>
							</div>
						)}
					</div>

					{!batch && <p className="importMuted">🔄 Loading batch…</p>}

					{batch && (
						<>
							<div className="batchStatLine">
								<span>
									Imported by: <b>{batch.uploaded_by ?? "—"}</b>
								</span>
								<span>
									Created: <b>{fmtDateTime(batch.created_at)}</b>
								</span>
								<span>
									Finished: <b>{fmtDateTime(batch.finished_at)}</b>
								</span>
								{outcome.stats.map(([label, value]) => (
									<span key={label}>
										{label}: <b>{value}</b>
									</span>
								))}
								{!outcome.contactMode &&
									(contactTotals.added > 0 || contactTotals.skipped > 0) && (
									<span>
										Contacts: <b>{contactTotals.added}</b> added
										{contactTotals.skipped > 0 && (
											<>
												{" "}· <b>{contactTotals.skipped}</b> skipped
											</>
										)}
									</span>
								)}
								{batch.reverted_by && (
									<span>
										Reverted by: <b>{batch.reverted_by}</b> at{" "}
										<b>{fmtDateTime(batch.finished_at)}</b>
									</span>
								)}
							</div>

							{outcome.summary.length > 0 && (
								<div className={`resultBanner ${outcome.chip.tone === "failed" ? "error" : "info"}`}>
									{outcome.summary.map((line) => (
										<div key={line}>{line}</div>
									))}
								</div>
							)}

							{status === "reverting" && (
								<div className="resultBanner info">
									🔄 Revert in progress — lead statuses below update live.
								</div>
							)}

							{/* Per-lead rows */}
							<div className="leadsHeader">
								<h3>Leads in this batch ({rows.length})</h3>
								{rowsWithContacts.length > 0 && (
									<button
										type="button"
										className="importBtn secondary small"
										onClick={toggleAllContacts}>
										{allContactsOpen ? "Hide all contacts" : "Show all contacts"}
									</button>
								)}
							</div>
							{rows.length === 0 ? (
								<p className="importMuted" style={{ fontStyle: "italic" }}>
									No per-lead rows returned for this batch.
								</p>
							) : (
								<div className="importTableWrap" style={{ maxHeight: 480, overflowY: "auto" }}>
									<table className="importTable">
										<thead>
											<tr>
												<th>Lead (group key)</th>
												<th>Iris lead id</th>
												<th>Action</th>
												<th>Contacts</th>
												<th>Status</th>
												<th>Imported at</th>
												<th>Error</th>
											</tr>
										</thead>
										<tbody>
											{rows.map((r, i) => {
												const key = r.id ?? i;
												const open = openContacts.has(key);
												return (
													<React.Fragment key={key}>
														<tr className={open ? "contactsOpenRow" : undefined}>
															<td>{r.group_key ?? "—"}</td>
															<td>
																<IrisLeadLink leadId={r.iris_lead_id} />
															</td>
															<td>{r.action ?? "—"}</td>
															<td>
																<ContactsCell
																	contacts={r.contacts}
																	open={open}
																	onToggle={() => toggleContacts(key)}
																/>
															</td>
															<td>
																<StatusChip
																	status={r.status}
																	{...leadRowChip(r.status, outcome.contactMode)}
																/>
															</td>
															<td>{fmtDateTime(r.imported_at)}</td>
															<td className="importError">{r.error || ""}</td>
														</tr>
														{open && (
															<tr className="contactsRow">
																<td colSpan={7}>
																	<ContactList contacts={r.contacts} />
																</td>
															</tr>
														)}
													</React.Fragment>
												);
											})}
										</tbody>
									</table>
								</div>
							)}

							{/* -------- Revert -------- */}
							<h3>Revert</h3>
							{!canScan && status !== "reverting" && (
								<p className="importMuted" style={{ fontSize: 13 }}>
									{status === "reverted"
										? "This batch has already been fully reverted."
										: "Revert becomes available once the batch has finished and has imported leads."}
								</p>
							)}
							{canScan && !scan && (
								<button
									type="button"
									className="importBtn secondary"
									onClick={startScan}
									disabled={scanLoading}>
									{scanLoading
										? "Scanning…"
										: "Scan batch for revert (no changes yet)"}
								</button>
							)}

							{scan && (
								<div>
									{/* Bucket 1: auto-revertible */}
									<div className="revertBucket autoOk">
										<div className="revertBucketTitle">
											✅ Auto-revertible ({scan.auto.length})
										</div>
										{scan.auto.length === 0 ? (
											<p className="importMuted" style={{ margin: 0 }}>
												No untouched leads to delete.
											</p>
										) : (
											<>
												<p style={{ marginTop: 0, fontSize: 13 }}>
													Untouched since import — these leads will be deleted
													from Iris.
												</p>
												<div className="chipRow">
													{scan.auto.map((l) => (
														<span
															key={l.importLeadId}
															className="columnChip"
															title={`Iris lead ${l.irisLeadId ?? "?"}`}>
															<IrisLeadLink
																leadId={l.irisLeadId}
																placeholder={leadDisplayName(l)}>
																{leadDisplayName(l)}
															</IrisLeadLink>
														</span>
													))}
												</div>
											</>
										)}
									</div>

									{/* Bucket 2: conflicts */}
									<div className="revertBucket conflictBucket">
										<div className="revertBucketTitle">
											⚠️ Conflicts — changed in Iris after import (
											{scan.conflicts.length})
										</div>
										{scan.conflicts.length === 0 ? (
											<p className="importMuted" style={{ margin: 0 }}>
												No conflicts.
											</p>
										) : (
											<>
												<div className="bulkBar">
													<label
														style={{
															display: "flex",
															alignItems: "center",
															gap: 6,
														}}>
														<input
															type="checkbox"
															checked={allChecked}
															onChange={toggleCheckAll}
														/>
														Select all
													</label>
													<span className="importMuted">
														{checked.size} selected
													</span>
													<button
														type="button"
														className="importBtn secondary small"
														onClick={() => bulkSetDecision("skip")}>
														Mark selected: Skip
													</button>
													<button
														type="button"
														className="importBtn danger small"
														onClick={() => bulkSetDecision("overwrite")}>
														Mark selected: Overwrite
													</button>
												</div>

												{scan.conflicts.map((c) => {
													const decision =
														decisions[c.importLeadId] || "skip";
													return (
														<div
															className="conflictCard"
															key={c.importLeadId}>
															<div className="conflictHeader">
																<label
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: 8,
																	}}>
																	<input
																		type="checkbox"
																		checked={checked.has(c.importLeadId)}
																		onChange={() =>
																			toggleChecked(c.importLeadId)
																		}
																	/>
																	<span className="conflictLeadName">
																		{leadDisplayName(c)}
																	</span>
																	<span
																		className="importMuted"
																		style={{ fontSize: 12 }}>
																		Iris lead{" "}
																		<IrisLeadLink
																			leadId={c.irisLeadId}
																			placeholder="?"
																		/>
																	</span>
																</label>
																<div className="decisionToggle">
																	<button
																		type="button"
																		className={
																			decision === "skip"
																				? "skipActive"
																				: ""
																		}
																		onClick={() =>
																			setDecision(c.importLeadId, "skip")
																		}>
																		Skip (keep lead)
																	</button>
																	<button
																		type="button"
																		className={
																			decision === "overwrite"
																				? "overwriteActive"
																				: ""
																		}
																		onClick={() =>
																			setDecision(
																				c.importLeadId,
																				"overwrite"
																			)
																		}>
																		Overwrite (revert anyway)
																	</button>
																</div>
															</div>

															{c.fields.length > 0 ? (
																<div className="importTableWrap">
																	<table className="importTable">
																		<thead>
																			<tr>
																				<th>Field</th>
																				<th>Value at import</th>
																				<th>Current value</th>
																				<th>Changed by</th>
																				<th>Source</th>
																			</tr>
																		</thead>
																		<tbody>
																			{c.fields.map((f, fi) => (
																				<tr key={fi}>
																					<td style={{ fontWeight: 600 }}>
																						{f.label}
																					</td>
																					<td>{f.importedValue || "—"}</td>
																					<td>{f.currentValue || "—"}</td>
																					<td>{f.changedBy || "—"}</td>
																					<td>{f.source || "—"}</td>
																				</tr>
																			))}
																		</tbody>
																	</table>
																</div>
															) : (
																<p
																	className="importMuted"
																	style={{ fontSize: 12, margin: 0 }}>
																	The server did not itemize the changed
																	fields for this lead.
																</p>
															)}
														</div>
													);
												})}
											</>
										)}
									</div>

									{/* Bucket 3: deleted externally */}
									<div className="revertBucket deletedBucket">
										<div className="revertBucketTitle">
											🗑 Deleted externally ({scan.deleted.length})
										</div>
										{scan.deleted.length === 0 ? (
											<p className="importMuted" style={{ margin: 0 }}>
												None.
											</p>
										) : (
											<>
												<p style={{ marginTop: 0, fontSize: 13 }}>
													Already deleted in Iris by someone else — nothing to
													do, they will be marked accordingly.
												</p>
												<div className="chipRow">
													{scan.deleted.map((l) => (
														<span
															key={l.importLeadId}
															className="columnChip"
															title={`Iris lead ${l.irisLeadId ?? "?"}`}>
															<IrisLeadLink
																leadId={l.irisLeadId}
																placeholder={leadDisplayName(l)}>
																{leadDisplayName(l)}
															</IrisLeadLink>
														</span>
													))}
												</div>
											</>
										)}
									</div>

									<div className="wizardNav">
										<button
											type="button"
											className="importBtn secondary"
											onClick={resetRevertState}>
											Cancel
										</button>
										<button
											type="button"
											className="importBtn danger"
											disabled={
												scan.auto.length === 0 && overwriteCount === 0
											}
											onClick={() => setConfirmOpen(true)}>
											Revert batch…
										</button>
									</div>
								</div>
							)}
						</>
					)}

					<ConfirmModal
						open={confirmOpen}
						title={`Revert batch #${selectedId}?`}
						confirmLabel="Revert now"
						busy={revertBusy}
						onConfirm={submitRevert}
						onCancel={() => setConfirmOpen(false)}>
						<p>This will permanently change Iris CRM data:</p>
						<ul>
							<li>
								<b>{scan ? scan.auto.length : 0}</b> untouched lead
								{scan && scan.auto.length === 1 ? "" : "s"} will be deleted
							</li>
							<li>
								<b>{overwriteCount}</b> conflicted lead
								{overwriteCount === 1 ? "" : "s"} will be{" "}
								<b>overwritten (deleted despite later edits)</b>
							</li>
							<li>
								<b>{skipCount}</b> conflicted lead
								{skipCount === 1 ? "" : "s"} will be skipped (kept in Iris)
							</li>
							<li>
								<b>{scan ? scan.deleted.length : 0}</b> already deleted
								externally (no action)
							</li>
						</ul>
						{overwriteCount > 0 && (
							<p className="importError">
								⚠ Overwrite deletes leads that were edited in Iris after the
								import — those edits will be lost.
							</p>
						)}
					</ConfirmModal>
				</div>
			)}
		</div>
	);
}
